import {
  ADMIN_BROADCAST_REASONS,
  ADMIN_BROADCAST_STATUSES,
  BROADCAST_BODY_MAX,
  BROADCAST_CREATE_CONFIRMATION,
  BROADCAST_IMAGE_ADVISORY_BYTES,
  BROADCAST_IMAGE_MIME_TYPES,
  BROADCAST_TITLE_MAX,
  type AdminBroadcast,
} from '@steado/admin-contract/broadcasts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImageIcon } from 'lucide-react';
import { useId, useRef, useState } from 'react';

import { Panel } from '@/components/panel/panels';
import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { TONES, type ToneName } from '@/components/table/tone';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/lib/auth/auth-context';
import { formatDateTime } from '@/lib/format';
import { scopeCountsQueryOptions } from '@/lib/scope-counts-query';
import { cn } from '@/lib/utils';

import {
  broadcastsQueryOptions,
  cancelBroadcast,
  createBroadcast,
  uploadBroadcastImage,
} from './broadcasts-query';
import { EMPTY_BROADCAST_FORM, formIssues, toCreateRequest } from './broadcast-form';
import type { BroadcastsSearch } from './broadcasts-search';

export interface BroadcastsScreenProps {
  readonly search: BroadcastsSearch;
  readonly onSearchChange: (next: BroadcastsSearch) => void;
}

const REASON_LABELS: Record<(typeof ADMIN_BROADCAST_REASONS)[number], string> = {
  PRODUCT_ANNOUNCEMENT: 'Product announcement',
  MAINTENANCE_NOTICE: 'Maintenance notice',
  POLICY_UPDATE: 'Policy update',
  INCIDENT_NOTICE: 'Incident notice',
};

const STATUS_LABELS: Record<AdminBroadcast['status'], string> = {
  SCHEDULED: 'Scheduled',
  ENQUEUING: 'Sending',
  COMPLETED: 'Sent',
  CANCELLED: 'Cancelled',
};

/** The pill was one neutral grey for every state; a status that reads as a
 *  status is the point of having tones. */
const STATUS_TONES: Record<AdminBroadcast['status'], ToneName> = {
  SCHEDULED: 'info',
  ENQUEUING: 'warn',
  COMPLETED: 'ok',
  CANCELLED: 'mute',
};

/**
 * Compose a push to every active user, and see what has been sent.
 *
 * The audience is everyone, so Send sits behind the phrase the contract
 * requires — typed out, in the composer itself rather than in a dialog that
 * appears after the decision is already made. Cancel exists only while a
 * broadcast is still SCHEDULED; once fan-out starts, the per-user work already
 * exists.
 */
export function BroadcastsScreen({ search, onSearchChange }: BroadcastsScreenProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const titleId = useId();
  const bodyId = useId();
  const reasonId = useId();
  const scheduleId = useId();
  const statusId = useId();
  const confirmId = useId();
  const [values, setValues] = useState(EMPTY_BROADCAST_FORM);
  const [confirmation, setConfirmation] = useState('');
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [oversizeImage, setOversizeImage] = useState(false);
  const [cancelling, setCancelling] = useState<AdminBroadcast | null>(null);

  const page = useQuery(broadcastsQueryOptions(api, search));
  // The audience, from the count the shell already holds. `users.all` is every
  // account that has not been deleted, which is exactly who a broadcast fans
  // out to.
  const counts = useQuery(scopeCountsQueryOptions(api));

  const create = useMutation({
    mutationFn: () => createBroadcast(api, toCreateRequest(values)),
    onSuccess: () => {
      setValues(EMPTY_BROADCAST_FORM);
      setConfirmation('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'broadcasts'] });
    },
    retry: false,
  });

  const uploadImage = useMutation({
    mutationFn: (file: File) => uploadBroadcastImage(api, file),
    onSuccess: (image) => {
      setValues((current) => ({ ...current, image }));
    },
    retry: false,
  });

  const cancel = useMutation({
    mutationFn: (id: string) => cancelBroadcast(api, id),
    onSuccess: () => {
      setCancelling(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'broadcasts'] });
    },
    retry: false,
  });

  const issues = formIssues(values);
  const scheduled = values.scheduledAtLocal !== '';
  const audience = counts.data?.users.all;
  const blocked = issues.length > 0 || !can.mutate || confirmation.trim() !== BROADCAST_CREATE_CONFIRMATION;

  return (
    <div className="grid max-w-[1200px] grid-cols-1 items-start gap-3 xl:grid-cols-[1.3fr_1fr]">
      <Panel
        title="Sent to everyone"
        bleed
        action={
          <Select
            value={search.status ?? ALL_STATUSES}
            onValueChange={(next) => {
              onSearchChange({
                status: next === ALL_STATUSES ? undefined : (next as BroadcastsSearch['status']),
                cursor: undefined,
              });
            }}
          >
            <SelectTrigger id={statusId} aria-label="Status" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_STATUSES}>All</SelectItem>
              {ADMIN_BROADCAST_STATUSES.map((status) => (
                <SelectItem key={status} value={status}>
                  {STATUS_LABELS[status]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      >
        {page.isError ? (
          <div className="border-t border-border px-[18px] py-3.5">
            <Alert role="alert" tone="err">
              <AlertDescription>{page.error.message}</AlertDescription>
            </Alert>
          </div>
        ) : null}

        {page.data === undefined ? (
          <p className="border-t border-border px-[18px] py-3.5 text-[13px] text-muted-foreground">
            Loading…
          </p>
        ) : page.data.items.length === 0 ? (
          <p className="border-t border-border px-[18px] py-3.5 text-[13px] text-muted-foreground">
            Nothing has been broadcast in this scope.
          </p>
        ) : (
          <ul>
            {page.data.items.map((broadcast) => (
              <li
                key={broadcast.id}
                className="flex flex-col gap-[7px] border-t border-border px-[18px] pt-3.5 pb-[15px]"
              >
                <div className="flex items-start gap-2.5">
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-[13px] font-semibold text-foreground">{broadcast.title}</span>
                    <span className="text-[13px] text-pretty text-muted-foreground">{broadcast.body}</span>
                  </div>
                  <span className="ml-auto flex shrink-0 items-center gap-2">
                    <span
                      className={cn(
                        'inline-flex h-5 items-center rounded-[10px] px-[7px] text-xs font-medium',
                        TONES[STATUS_TONES[broadcast.status]],
                      )}
                    >
                      {STATUS_LABELS[broadcast.status]}
                    </span>
                    {broadcast.status === 'SCHEDULED' && can.mutate ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        onClick={() => {
                          setCancelling(broadcast);
                        }}
                        className="text-err-fg hover:text-err-fg"
                      >
                        Cancel
                      </Button>
                    ) : null}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2.5 text-xs text-muted-foreground">
                  <span>{whenOf(broadcast)}</span>
                  <Slash />
                  {/*
                    The reason was always collected and always audited; it is on
                    the row now because "what have we been pushing to everyone"
                    is a question about the broadcasts, and answering it from
                    the audit trail is a trip nobody makes.
                  */}
                  <span>{REASON_LABELS[broadcast.reason]}</span>
                  <Slash />
                  <span className="font-mono">{progressOf(broadcast)}</span>
                  {broadcast.image === null ? null : broadcast.image.url === null ? (
                    <span className="text-warn-fg">image has no public URL</span>
                  ) : (
                    <span>with an image</span>
                  )}
                </div>

                {/*
                  Fan-out is per user and takes minutes on a large audience, so
                  the bar is the only thing that says whether a "Sending" row is
                  moving. Decorative: the same numbers are already in the line
                  above it.
                */}
                <div aria-hidden="true" className="h-[5px] overflow-hidden rounded-[3px] bg-secondary">
                  <div
                    className="h-full rounded-[3px] bg-primary"
                    style={{ width: `${String(percentOf(broadcast))}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}

        {page.data?.pagination.hasMore === true ? (
          <div className="flex justify-end border-t border-border px-[18px] py-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                onSearchChange({ ...search, cursor: page.data?.pagination.nextCursor ?? undefined });
              }}
            >
              Older broadcasts
            </Button>
          </div>
        ) : null}
      </Panel>

      <Panel
        title="Compose"
        bleed
        footer={
          <div className="flex flex-col gap-2.5">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={confirmId} className="text-[12.5px] font-medium text-err-fg">
                Type {BROADCAST_CREATE_CONFIRMATION} to confirm
              </Label>
              <Input
                id={confirmId}
                value={confirmation}
                disabled={!can.mutate}
                autoComplete="off"
                onChange={(event) => {
                  setConfirmation(event.target.value);
                }}
                placeholder={BROADCAST_CREATE_CONFIRMATION}
                className="h-8 font-mono text-[13px]"
              />
            </div>

            {issues.length === 0 ? null : (
              <p className="text-xs text-muted-foreground">Before this can go out: {issues.join(' ')}</p>
            )}
            {create.isError ? (
              <p role="alert" className="text-xs text-err-fg">
                {create.error.message}
              </p>
            ) : null}

            <Button
              type="button"
              className="h-8 w-full"
              disabled={blocked || create.isPending}
              title={can.mutate ? undefined : ADMIN_ONLY}
              onClick={() => {
                create.mutate();
              }}
            >
              {create.isPending
                ? 'Sending…'
                : scheduled
                  ? 'Schedule for everyone'
                  : audience === undefined
                    ? 'Send to everyone'
                    : `Send to ${audience.toLocaleString()} ${audience === 1 ? 'person' : 'people'}`}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3 border-t border-border px-[18px] pt-3.5 pb-4">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline gap-2">
              <Label htmlFor={titleId} className="text-[12.5px] font-medium text-muted-foreground">
                Title
              </Label>
              <span className="ml-auto font-mono text-xs text-muted-foreground">
                {values.title.length} / {BROADCAST_TITLE_MAX}
              </span>
            </div>
            <Input
              id={titleId}
              value={values.title}
              disabled={!can.mutate}
              maxLength={BROADCAST_TITLE_MAX}
              onChange={(event) => {
                setValues({ ...values, title: event.target.value });
              }}
              placeholder="What people see first"
              className="h-8"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline gap-2">
              <Label htmlFor={bodyId} className="text-[12.5px] font-medium text-muted-foreground">
                Body
              </Label>
              <span className="ml-auto font-mono text-xs text-muted-foreground">
                {values.body.length} / {BROADCAST_BODY_MAX}
              </span>
            </div>
            <Textarea
              id={bodyId}
              value={values.body}
              disabled={!can.mutate}
              maxLength={BROADCAST_BODY_MAX}
              rows={3}
              onChange={(event) => {
                setValues({ ...values, body: event.target.value });
              }}
              placeholder="One sentence. It has to make sense on a lock screen with no context."
            />
          </div>

          <div className="flex flex-col gap-1.5">
            {/* Radix draws its own listbox, so the trigger carries the name
                itself: a `for` on a button is not what names it. */}
            <Label htmlFor={reasonId} className="text-[12.5px] font-medium text-muted-foreground">
              Reason
            </Label>
            <Select
              value={values.reason}
              disabled={!can.mutate}
              onValueChange={(next) => {
                setValues({
                  ...values,
                  reason: next as (typeof ADMIN_BROADCAST_REASONS)[number],
                });
              }}
            >
              <SelectTrigger id={reasonId} aria-label="Reason" size="sm" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ADMIN_BROADCAST_REASONS.map((reason) => (
                  <SelectItem key={reason} value={reason}>
                    {REASON_LABELS[reason]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={scheduleId} className="text-[12.5px] font-medium text-muted-foreground">
              When, in your local time
            </Label>
            <DateTimePicker
              id={scheduleId}
              value={values.scheduledAtLocal}
              disabled={!can.mutate}
              onChange={(scheduledAtLocal) => {
                setValues({ ...values, scheduledAtLocal });
              }}
              placeholder="Send now"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="text-[12.5px] font-medium text-muted-foreground">Image, optional</Label>
            {values.image === null ? (
              <>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept={BROADCAST_IMAGE_MIME_TYPES.join(',')}
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) {
                      setOversizeImage(file.size > BROADCAST_IMAGE_ADVISORY_BYTES);
                      uploadImage.mutate(file);
                    }
                  }}
                />
                <button
                  type="button"
                  disabled={!can.mutate || uploadImage.isPending}
                  onClick={() => imageInputRef.current?.click()}
                  className="flex min-h-[52px] cursor-pointer items-center gap-2.5 rounded-xl border border-dashed border-input bg-card px-2.5 py-2 text-left text-[13px] text-muted-foreground hover:border-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ImageIcon aria-hidden="true" className="size-4 shrink-0" />
                  <span className="text-pretty">
                    {uploadImage.isPending
                      ? 'Uploading…'
                      : 'jpeg, png or webp, under 1 MB. Android announcements only.'}
                  </span>
                </button>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <img
                  src={values.image.url ?? ''}
                  alt="Attached broadcast image"
                  className="size-12 rounded border border-border object-cover"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  disabled={!can.mutate}
                  onClick={() => {
                    setValues({ ...values, image: null });
                  }}
                >
                  Remove
                </Button>
                {oversizeImage ? (
                  <p className="text-xs text-warn-fg">
                    Over 1 MB. It will send, but Android may not draw it.
                  </p>
                ) : null}
              </div>
            )}
            {uploadImage.isError ? <p className="text-xs text-err-fg">{uploadImage.error.message}</p> : null}
          </div>
        </div>
      </Panel>

      {cancelling !== null ? (
        <ConfirmDialog
          title="Cancel this broadcast?"
          body={`"${cancelling.title}" is scheduled for ${formatDateTime(cancelling.scheduledAt)} and has not gone out. Cancelling keeps the record but nothing will be sent.`}
          confirmLabel="Cancel broadcast"
          destructive
          onConfirm={() => {
            cancel.mutate(cancelling.id);
          }}
          onCancel={() => {
            setCancelling(null);
            cancel.reset();
          }}
          isPending={cancel.isPending}
          error={cancel.isError ? cancel.error.message : undefined}
        />
      ) : null}
    </div>
  );
}

/** The separator between the facts on a broadcast's meta line. */
function Slash() {
  return (
    <span aria-hidden="true" className="text-input">
      /
    </span>
  );
}

/** The one date that matters for this broadcast's state. */
function whenOf(broadcast: AdminBroadcast): string {
  if (broadcast.status === 'SCHEDULED') return `Goes out ${formatDateTime(broadcast.scheduledAt)}`;
  if (broadcast.status === 'CANCELLED') {
    return `Cancelled ${formatDateTime(broadcast.cancelledAt ?? broadcast.scheduledAt)}`;
  }
  if (broadcast.completedAt !== null) return `Finished ${formatDateTime(broadcast.completedAt)}`;
  return `Started ${formatDateTime(broadcast.startedAt ?? broadcast.scheduledAt)}`;
}

/**
 * How far fan-out has got.
 *
 * `totalUserCount` is null until the send starts and the audience is counted,
 * so before that there is a denominator-shaped hole and saying "0 / 0" would
 * invent one.
 */
function progressOf(broadcast: AdminBroadcast): string {
  if (broadcast.totalUserCount === null) {
    return broadcast.enqueuedUserCount === 0
      ? 'not counted yet'
      : `${String(broadcast.enqueuedUserCount)} queued`;
  }
  return `${String(broadcast.enqueuedUserCount)} / ${String(broadcast.totalUserCount)}`;
}

function percentOf(broadcast: AdminBroadcast): number {
  if (broadcast.status === 'CANCELLED') return 0;
  if (broadcast.totalUserCount === null || broadcast.totalUserCount === 0) {
    return broadcast.status === 'COMPLETED' ? 100 : 0;
  }
  return Math.min(100, Math.round((broadcast.enqueuedUserCount / broadcast.totalUserCount) * 100));
}

const ADMIN_ONLY = 'Sending a broadcast requires the admin role.';

/**
 * Radix has no concept of an empty option — `value=""` clears the select
 * rather than selecting something — so "no filter" needs a value of its own.
 */
const ALL_STATUSES = '__all__';
